/**
 * Surfaces and switchers: the panel and the card, the toolbar row, tabs
 * (segmented or underline, with a sliding thumb) and the segmented control,
 * and the inline callout. Re-exported from `Ui.tsx`; screens import from there.
 */
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react'

import { Count, cx, toneClass, type Tone } from './primitives'
import styles from './Surfaces.module.css'

/* ------------------------------------------------------------------------ */
/* panel / card                                                             */
/* ------------------------------------------------------------------------ */

/** A bare surface (surface-1, hairline, radius 12) with no padding: tables and custom blocks go inside. */
export function Panel({ children, className, style }: { children: ReactNode; className?: string; style?: CSSProperties }): JSX.Element {
  return (
    <div className={cx(styles.panel, className)} style={style}>
      {children}
    </div>
  )
}

export interface CardProps {
  /** Section title in the header (15/20 semibold). */
  title?: ReactNode
  /** One quiet line under the title. */
  description?: ReactNode
  /** Small uppercase caption above the title. */
  eyebrow?: ReactNode
  /** A Lucide icon in a tile left of the title. */
  icon?: ReactNode
  /** Right side of the header: buttons, a badge, a link. */
  actions?: ReactNode
  /** A hairline-topped strip under the body. */
  footer?: ReactNode
  /** Body without padding (a table, a list that runs edge to edge). */
  flush?: boolean
  /** Hover lift for a card that is a link or opens something. */
  interactive?: boolean
  className?: string
  bodyClassName?: string
  style?: CSSProperties
  children?: ReactNode
}

/** A card: optional header (icon, eyebrow, title, description, actions), padded body (or `flush`), optional footer. */
export function Card({ title, description, eyebrow, icon, actions, footer, flush = false, interactive = false, className, bodyClassName, style, children }: CardProps): JSX.Element {
  const hasHead = Boolean(title || description || eyebrow || actions || icon)
  return (
    <section className={cx(styles.panel, styles.card, interactive ? styles.interactive : '', className)} style={style}>
      {hasHead ? (
        <header className={cx(styles.cardHead, children !== undefined && flush ? styles.cardHeadRule : '')}>
          {icon ? <span className={styles.cardIcon}>{icon}</span> : null}
          <div className={styles.cardHeadText}>
            {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
            {title ? <h2 className={styles.cardTitle}>{title}</h2> : null}
            {description ? <p className={styles.cardDescription}>{description}</p> : null}
          </div>
          {actions ? <div className={styles.cardActions}>{actions}</div> : null}
        </header>
      ) : null}
      {children !== undefined && children !== null ? (
        <div className={cx(flush ? styles.cardFlush : styles.cardBody, hasHead && !flush ? styles.cardBodyAfterHead : '', bodyClassName)}>{children}</div>
      ) : null}
      {footer ? <footer className={styles.cardFoot}>{footer}</footer> : null}
    </section>
  )
}

/* ------------------------------------------------------------------------ */
/* toolbar                                                                  */
/* ------------------------------------------------------------------------ */

/** A row of filters and actions above a table (wraps; 12 px gaps; 16 px below). */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={cx(styles.toolbar, className)}>{children}</div>
}

/** Pushes what follows to the far end of a Toolbar or a footer row. */
export function Spacer(): JSX.Element {
  return <div className={styles.spacer} />
}

/* ------------------------------------------------------------------------ */
/* tabs / segmented                                                         */
/* ------------------------------------------------------------------------ */

export interface TabOption<T extends string> {
  value: T
  label: ReactNode
  /** A small counter pill after the label. */
  count?: number
  /** The counter's tone (warning for a queue waiting on the operator). */
  tone?: Tone
  /** A leading 16 px icon. */
  icon?: ReactNode
  disabled?: boolean
}

interface SwitcherProps<T extends string> {
  value: T
  options: readonly TabOption<T>[]
  onChange: (value: T) => void
  kind: 'tabs' | 'radio'
  variant: 'segmented' | 'underline'
  size: 'sm' | 'md'
  ariaLabel?: string
  block?: boolean
  className?: string
}

/** The engine under Tabs and SegmentedControl: roving focus with arrows/Home/End, a thumb that slides to the active item. */
function Switcher<T extends string>({ value, options, onChange, kind, variant, size, ariaLabel, block = false, className }: SwitcherProps<T>): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null)
  const [thumb, setThumb] = useState<{ x: number; w: number } | null>(null)
  const [animate, setAnimate] = useState(false)

  const measure = useCallback(() => {
    const list = listRef.current
    if (!list) return
    const active = list.querySelector<HTMLElement>('[data-active="true"]')
    if (!active) {
      setThumb(null)
      return
    }
    setThumb((current) => {
      const next = { x: active.offsetLeft, w: active.offsetWidth }
      return current && current.x === next.x && current.w === next.w ? current : next
    })
  }, [])

  // Inline option arrays are new on every render; their values are what matters.
  const shape = options.map((option) => `${option.value}:${option.count ?? ''}`).join('|')
  useLayoutEffect(() => {
    measure()
  }, [measure, value, shape])

  useLayoutEffect(() => {
    const list = listRef.current
    if (!list || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measure())
    observer.observe(list)
    list.querySelectorAll('button').forEach((button) => observer.observe(button))
    // Web fonts change label widths after the first paint.
    void document.fonts?.ready.then(() => measure())
    return () => observer.disconnect()
  }, [measure, shape])

  // The first placement lands without a slide; later changes slide.
  useLayoutEffect(() => {
    if (thumb && !animate) {
      const frame = requestAnimationFrame(() => setAnimate(true))
      return () => cancelAnimationFrame(frame)
    }
    return undefined
  }, [thumb, animate])

  const enabled = options.filter((option) => !option.disabled)
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (!keys.includes(event.key) || enabled.length === 0) return
    event.preventDefault()
    const index = enabled.findIndex((option) => option.value === value)
    const next =
      event.key === 'Home'
        ? enabled[0]
        : event.key === 'End'
          ? enabled[enabled.length - 1]
          : enabled[(index + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length]
    onChange(next.value)
    requestAnimationFrame(() => listRef.current?.querySelector<HTMLElement>(`[data-value="${CSS.escape(next.value)}"]`)?.focus())
  }

  const itemRole = kind === 'tabs' ? 'tab' : 'radio'
  return (
    <div
      ref={listRef}
      role={kind === 'tabs' ? 'tablist' : 'radiogroup'}
      aria-label={ariaLabel}
      className={cx(styles.switcher, variant === 'underline' ? styles.underline : styles.segmented, size === 'sm' ? styles.switcherSm : '', block ? styles.switcherBlock : '', className)}
      onKeyDown={onKeyDown}
    >
      <span
        className={cx(styles.thumb, animate ? styles.thumbAnimate : '')}
        aria-hidden="true"
        style={thumb ? ({ '--x': `${thumb.x}px`, '--w': `${thumb.w}px` } as CSSProperties) : { opacity: 0 }}
      />
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role={itemRole}
            aria-selected={kind === 'tabs' ? active : undefined}
            aria-checked={kind === 'radio' ? active : undefined}
            tabIndex={active ? 0 : -1}
            disabled={option.disabled}
            data-active={active}
            data-value={option.value}
            className={styles.item}
            onClick={() => onChange(option.value)}
          >
            {option.icon ? <span className={styles.itemIcon}>{option.icon}</span> : null}
            <span className={styles.itemLabel}>{option.label}</span>
            {option.count !== undefined ? (
              <Count tone={option.tone ?? 'neutral'} className={cx(styles.itemCount, option.tone === undefined || option.tone === 'neutral' ? styles.itemCountNeutral : '')}>
                {option.count}
              </Count>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export interface TabsProps<T extends string> {
  value: T
  options: readonly TabOption<T>[]
  onChange: (value: T) => void
  /** segmented (default): a pill group with a sliding thumb, for filters above a table · underline: page-level tabs on a hairline with a sliding 2 px accent bar. */
  variant?: 'segmented' | 'underline'
  size?: 'sm' | 'md'
  ariaLabel?: string
  className?: string
}

/** Tabs (role=tablist): arrow keys move between them; the thumb or underline slides 240 ms to the selected one. */
export function Tabs<T extends string>({ value, options, onChange, variant = 'segmented', size = 'md', ariaLabel, className }: TabsProps<T>): JSX.Element {
  return <Switcher value={value} options={options} onChange={onChange} kind="tabs" variant={variant} size={size} ariaLabel={ariaLabel} className={className} />
}

export interface SegmentedControlProps<T extends string> {
  value: T
  options: readonly TabOption<T>[]
  onChange: (value: T) => void
  size?: 'sm' | 'md'
  /** Stretch to the container, items share the width. */
  block?: boolean
  ariaLabel?: string
  className?: string
}

/** A single-choice switch (role=radiogroup) for a mode or a period: «24 ч · 7 д · 30 д»; the thumb slides. */
export function SegmentedControl<T extends string>({ value, options, onChange, size = 'md', block = false, ariaLabel, className }: SegmentedControlProps<T>): JSX.Element {
  return <Switcher value={value} options={options} onChange={onChange} kind="radio" variant="segmented" size={size} block={block} ariaLabel={ariaLabel} className={className} />
}

/* ------------------------------------------------------------------------ */
/* callout                                                                  */
/* ------------------------------------------------------------------------ */

export interface CalloutProps {
  /** info (default) · warning · down (a problem) · accent (all good) · neutral. */
  tone?: Tone
  title?: ReactNode
  children?: ReactNode
  /** Replaces the tone's default icon; `false` hides it. */
  icon?: ReactNode | false
  /** A button or link on the right. */
  action?: ReactNode
  className?: string
}

const CALLOUT_ICON: Record<Tone, ReactNode> = {
  info: <Info />,
  neutral: <Info />,
  warning: <TriangleAlert />,
  down: <CircleAlert />,
  accent: <CircleCheck />,
}

/** An inline notice in a tone (wash fill, edge, icon): a switched-off safeguard, a rule to know before acting. */
export function Callout({ tone = 'info', title, children, icon, action, className }: CalloutProps): JSX.Element {
  const shownIcon = icon === false ? null : (icon ?? CALLOUT_ICON[tone])
  return (
    <div className={cx(styles.callout, toneClass(tone), className)} role={tone === 'down' ? 'alert' : undefined}>
      {shownIcon ? <span className={styles.calloutIcon}>{shownIcon}</span> : null}
      <div className={styles.calloutText}>
        {title ? <div className={styles.calloutTitle}>{title}</div> : null}
        {children ? <div className={styles.calloutBody}>{children}</div> : null}
      </div>
      {action ? <div className={styles.calloutAction}>{action}</div> : null}
    </div>
  )
}
